import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Image, Spin } from "antd";
import { ExclamationCircleOutlined, LikeFilled, LikeOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import { observer } from "mobx-react-lite";
import { useNavigate, useLocation } from "react-router-dom";
import { globalModel } from "../../global/GlobalModel";
import { showConceptLibraryDesktopTipModal } from "../../space/common/DesktopOnlyTipModal";
import { h5NavigateTo } from "../../utils/miniProgramEnv";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { resolveAppAvatarUrl } from "../../utils/resolveAppMediaUrl";
import { useMainScrollPullRefresh } from "../../utils/useMainScrollPullRefresh";
import { useTabsRowTouchIsolation } from "../../utils/useTabsRowTouchIsolation";
import { useTabsScrollFadeRight } from "../../utils/useTabsScrollFadeRight";
import { ConceptApi } from "../concept/ConceptApi";
import type { ConceptBrief } from "../concept/ConceptType";
import { viewpointFeedLayoutCssVars } from "./Config";
import { ViewpointConceptLinkedBody, ViewpointConceptTagRow } from "./ViewpointConceptContent";
import { ViewpointFeedApi } from "./ViewpointFeedApi";
import {
  clearViewpointFeedCache,
  loadViewpointFeedCache,
  saveViewpointFeedCache,
  trimViewpointFeedStream,
  updateViewpointFeedCacheItem,
} from "./ViewpointFeedLocalStore";
import type {
  ViewpointFeedCacheScope,
  ViewpointFeedCategoryItem,
  ViewpointFeedListKind,
  ViewpointFeedPost,
} from "./ViewpointFeedTypes";
import "./ViewpointFeedPage.css";
import { ViewpointHotBadge } from "./ViewpointHotBadge";
import { ViewpointReportModal } from "./ViewpointReportModal";
import {
  buildViewpointDisplayItems,
  formatViewpointEngagementCount,
  mergeFeedIncoming,
} from "./ViewpointUtil";

const PAGE_SIZE = 8;
const POLL_MS = 5000;
const VP_FEED_BODY_SCROLL_SELECTOR = ".lf-vp-feed__body";

function feedQueryExtras(categoryId: string | null) {
  return categoryId ? { categoryId } : {};
}

function normalizeFeedPost(p: ViewpointFeedPost): ViewpointFeedPost {
  return {
    ...p,
    likeCount: p.likeCount ?? 0,
    collectCount: p.collectCount ?? 0,
  };
}

function normalizePinnedPost(p: ViewpointFeedPost): ViewpointFeedPost {
  return { ...normalizeFeedPost(p), pinned: true };
}

function normalizeFeedOnlyPost(p: ViewpointFeedPost): ViewpointFeedPost {
  return { ...normalizeFeedPost(p), pinned: false };
}

function ViewpointFeedReportAction({
  row,
  onReport,
}: {
  row: ViewpointFeedPost;
  onReport: (e: React.MouseEvent, row: ViewpointFeedPost) => void;
}) {
  return (
    <button
      type="button"
      className="lf-vp-report-action-btn"
      onClick={(e) => {
        e.stopPropagation();
        onReport(e, row);
      }}
      aria-label="举报此内容"
    >
      <ExclamationCircleOutlined className="lf-vp-report-action-ic" />
    </button>
  );
}

function ViewpointFeedEngagementActions({
  row,
  onToggleLike,
  onToggleCollect,
}: {
  row: ViewpointFeedPost;
  onToggleLike: (e: React.MouseEvent, row: ViewpointFeedPost) => void;
  onToggleCollect: (e: React.MouseEvent, row: ViewpointFeedPost) => void;
}) {
  const liked = Boolean(row.likedByMe);
  const collected = Boolean(row.collectedByMe);
  return (
    <div className="lf-vp-moment-actions" aria-label="点赞与收藏">
      <button
        type="button"
        className="lf-vp-moment-action-btn"
        onClick={(e) => void onToggleLike(e, row)}
        aria-label={liked ? "取消点赞" : "点赞"}
      >
        {liked ? (
          <LikeFilled className="lf-vp-moment-action-ic lf-vp-moment-action-ic--on" />
        ) : (
          <LikeOutlined className="lf-vp-moment-action-ic" />
        )}
        <span>{formatViewpointEngagementCount(row.likeCount ?? 0)}</span>
      </button>
      <button
        type="button"
        className="lf-vp-moment-action-btn"
        onClick={(e) => void onToggleCollect(e, row)}
        aria-label={collected ? "取消收藏" : "收藏"}
      >
        {collected ? (
          <StarFilled className="lf-vp-moment-action-ic lf-vp-moment-action-ic--collect-on" />
        ) : (
          <StarOutlined className="lf-vp-moment-action-ic" />
        )}
        <span>{formatViewpointEngagementCount(row.collectCount ?? 0)}</span>
      </button>
    </div>
  );
}

function displayPublishedCompact(v: unknown): string {
  if (v == null || v === "") return "";
  if (v instanceof Date) return dayjs(v).format("M月D日 HH:mm");
  if (typeof v === "string") {
    const t = v.trim();
    const d = dayjs(t);
    return d.isValid() ? d.format("M月D日 HH:mm") : t;
  }
  return String(v).trim();
}

function nicknameOf(row: ViewpointFeedPost): string {
  const n = row.publisherNickname?.trim();
  if (n) return n;
  return row.publisherLabel?.trim() || "用户";
}

function refreshedAtLabel(t: Date | null): string {
  if (!t) return "—";
  return dayjs(t).format("M月D日 HH:mm");
}

type FeedListFilter = ViewpointFeedListKind;

function importanceClass(imp: string): string {
  if (imp === "urgent") return "lf-vp-moment-imp lf-vp-moment-imp--urgent";
  if (imp === "important") return "lf-vp-moment-imp lf-vp-moment-imp--important";
  return "lf-vp-moment-imp lf-vp-moment-imp--normal";
}

function importanceLabel(imp: string): string {
  if (imp === "urgent") return "精选";
  if (imp === "important") return "重要";
  return "";
}

function pullRefreshHint(phase: string): string {
  if (phase === "refreshing") return "刷新中…";
  if (phase === "ready") return "松开刷新";
  if (phase === "pulling") return "下拉刷新";
  return "";
}

export interface ViewpointFeedPageProps {
  spaceId: string;
  spaceProductId: string;
  spaceDisplayName: string;
  /** 同空间概念产品 ID，用于正文概念识别 */
  conceptSpaceProductId?: string;
}

const ViewpointFeedPage: React.FC<ViewpointFeedPageProps> = observer(
  ({ spaceId, spaceProductId, conceptSpaceProductId }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [pinnedItems, setPinnedItems] = useState<ViewpointFeedPost[]>([]);
    const [feedItems, setFeedItems] = useState<ViewpointFeedPost[]>([]);
    const [hasMoreOlder, setHasMoreOlder] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [feedFilter, setFeedFilter] = useState<FeedListFilter>("all");
    const [feedCategories, setFeedCategories] = useState<ViewpointFeedCategoryItem[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [conceptCatalog, setConceptCatalog] = useState<ConceptBrief[]>([]);
    const [reportTarget, setReportTarget] = useState<ViewpointFeedPost | null>(null);
    const [reportOpen, setReportOpen] = useState(false);
    const pinnedRef = useRef<ViewpointFeedPost[]>([]);
    const feedRef = useRef<ViewpointFeedPost[]>([]);
    const hasMoreOlderRef = useRef(false);
    const refreshedAtRef = useRef<Date | null>(null);
    pinnedRef.current = pinnedItems;
    feedRef.current = feedItems;
    hasMoreOlderRef.current = hasMoreOlder;
    refreshedAtRef.current = refreshedAt;
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const bodyScrollRef = useRef<HTMLDivElement | null>(null);
    const tabsRowRef = useRef<HTMLDivElement | null>(null);
    const tabsScrollRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      setSelectedCategoryId(null);
      setFeedFilter("all");
    }, [spaceId, spaceProductId]);

    useEffect(() => {
      void (async () => {
        const res = await ViewpointFeedApi.listViewpointCategories({ spaceId, spaceProductId });
        if (typeof res === "string") {
          setFeedCategories([]);
          return;
        }
        setFeedCategories(Array.isArray(res.list) ? res.list : []);
      })();
    }, [spaceId, spaceProductId]);

    useEffect(() => {
      const pid = conceptSpaceProductId?.trim();
      if (!pid) {
        setConceptCatalog([]);
        return;
      }
      void (async () => {
        try {
          const list = await ConceptApi.listConcepts(pid);
          setConceptCatalog(Array.isArray(list) ? list : []);
        } catch {
          setConceptCatalog([]);
        }
      })();
    }, [conceptSpaceProductId]);

    const cacheScope = useMemo((): ViewpointFeedCacheScope | null => {
      const customerId = globalModel.currentUser?.id?.trim();
      if (!customerId) {
        return null;
      }
      return {
        customerId,
        spaceId,
        spaceProductId,
        categoryId: feedFilter === "all" ? selectedCategoryId : null,
        listKind: feedFilter,
      };
    }, [spaceId, spaceProductId, selectedCategoryId, feedFilter]);

    const applyRemoteSnapshot = useCallback(
      (
        nextPinned: ViewpointFeedPost[],
        nextFeed: ViewpointFeedPost[],
        nextHasMore: boolean,
        now: Date
      ) => {
        setPinnedItems(nextPinned);
        setFeedItems(nextFeed);
        setHasMoreOlder(nextHasMore);
        setRefreshedAt(now);
        if (cacheScope) {
          saveViewpointFeedCache(cacheScope, nextPinned, nextFeed, nextHasMore, now);
        }
      },
      [cacheScope]
    );

    const fetchPinnedRemote = useCallback(async () => {
      if (feedFilter !== "all") {
        return [] as ViewpointFeedPost[];
      }
      const res = await ViewpointFeedApi.getPinnedViewpoints({
        spaceId,
        spaceProductId,
        ...feedQueryExtras(selectedCategoryId),
      });
      if (typeof res === "string") {
        return [] as ViewpointFeedPost[];
      }
      return (res.list ?? []).map(normalizePinnedPost);
    }, [spaceId, spaceProductId, selectedCategoryId, feedFilter]);

    const syncNewer = useCallback(async () => {
      setSyncing(true);
      try {
        const prevFeed = feedRef.current;
        const top = prevFeed[0];
        const extras = feedFilter === "all" ? feedQueryExtras(selectedCategoryId) : {};
        const [latestRes, nextPinned] = await Promise.all([
          top
            ? ViewpointFeedApi.getLatestViewpoints(
                {
                  spaceId,
                  spaceProductId,
                  limit: PAGE_SIZE,
                  cursorPublishedAt: top.publishedAt,
                  cursorId: top.id,
                  ...extras,
                },
                feedFilter
              )
            : Promise.resolve({ list: [], hasMore: false }),
          fetchPinnedRemote(),
        ]);
        const now = new Date();
        let nextFeed = prevFeed;
        if (typeof latestRes !== "string" && latestRes.list.length) {
          nextFeed = mergeFeedIncoming(
            prevFeed,
            latestRes.list.map(normalizeFeedOnlyPost)
          );
        }
        const trimmedFeed = cacheScope ? trimViewpointFeedStream(nextFeed) : nextFeed;
        applyRemoteSnapshot(nextPinned, trimmedFeed, hasMoreOlderRef.current, now);
      } finally {
        setSyncing(false);
      }
    }, [
      applyRemoteSnapshot,
      cacheScope,
      fetchPinnedRemote,
      spaceId,
      spaceProductId,
      selectedCategoryId,
      feedFilter,
    ]);

    const loadFirstPageRemote = useCallback(async () => {
      const extras = feedFilter === "all" ? feedQueryExtras(selectedCategoryId) : {};
      const [feedRes, nextPinned] = await Promise.all([
        ViewpointFeedApi.getOldViewpoints(
          {
            spaceId,
            spaceProductId,
            limit: PAGE_SIZE,
            ...extras,
          },
          feedFilter
        ),
        fetchPinnedRemote(),
      ]);
      if (typeof feedRes === "string") {
        return false;
      }
      const now = new Date();
      const nextFeed = feedRes.list.map(normalizeFeedOnlyPost);
      applyRemoteSnapshot(nextPinned, nextFeed, feedRes.hasMore, now);
      return true;
    }, [
      applyRemoteSnapshot,
      fetchPinnedRemote,
      spaceId,
      spaceProductId,
      selectedCategoryId,
      feedFilter,
    ]);

    const bootstrapFeed = useCallback(async () => {
      const cached = cacheScope ? loadViewpointFeedCache(cacheScope) : null;
      if (cached) {
        setPinnedItems(cached.pinned.map(normalizePinnedPost));
        setFeedItems(cached.feed.map(normalizeFeedOnlyPost));
        setHasMoreOlder(cached.hasMoreOlder);
        setRefreshedAt(cached.refreshedAt ? new Date(cached.refreshedAt) : null);
      } else {
        setLoading(true);
      }

      try {
        const ok = await loadFirstPageRemote();
        if (!ok && !cached) {
          setPinnedItems([]);
          setFeedItems([]);
          setHasMoreOlder(false);
          if (cacheScope) {
            clearViewpointFeedCache(cacheScope);
          }
        }
      } finally {
        setLoading(false);
      }
    }, [cacheScope, loadFirstPageRemote]);

    const forceRefresh = useCallback(async () => {
      if (cacheScope) {
        clearViewpointFeedCache(cacheScope);
      }
      setLoading(true);
      try {
        const ok = await loadFirstPageRemote();
        if (!ok) {
          setPinnedItems([]);
          setFeedItems([]);
          setHasMoreOlder(false);
        }
      } finally {
        setLoading(false);
      }
    }, [cacheScope, loadFirstPageRemote]);

    const { pullDistance, phase: pullPhase } = useMainScrollPullRefresh(
      forceRefresh,
      true,
      VP_FEED_BODY_SCROLL_SELECTOR
    );
    useTabsRowTouchIsolation(tabsRowRef, tabsScrollRef);

    const scrollListToTop = useCallback(() => {
      bodyScrollRef.current?.scrollTo({ top: 0 });
    }, []);

    const scrollActiveTabIntoView = useCallback(() => {
      const scrollEl = tabsScrollRef.current;
      if (!scrollEl) {
        return;
      }
      const active = scrollEl.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      if (!active) {
        return;
      }
      scrollEl.scrollTo({ left: Math.max(0, active.offsetLeft - 4), behavior: "smooth" });
    }, []);

    const displayItems = useMemo(() => {
      if (feedFilter === "all") {
        return buildViewpointDisplayItems(pinnedItems, feedItems);
      }
      return buildViewpointDisplayItems([], feedItems);
    }, [pinnedItems, feedItems, feedFilter]);

    useEffect(() => {
      void bootstrapFeed();
    }, [bootstrapFeed]);

    useEffect(() => {
      scrollListToTop();
      const raf = requestAnimationFrame(scrollActiveTabIntoView);
      return () => cancelAnimationFrame(raf);
    }, [selectedCategoryId, feedFilter, scrollListToTop, scrollActiveTabIntoView]);

    useEffect(() => {
      const el = sentinelRef.current;
      const root = bodyScrollRef.current;
      if (!el || !root) return undefined;
      const ob = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry?.isIntersecting || loading || loadingMore) return;
          if (!hasMoreOlder) return;
          const last = feedRef.current[feedRef.current.length - 1];
          if (!last) return;
          setLoadingMore(true);
          void (async () => {
            try {
              const res = await ViewpointFeedApi.getOldViewpoints(
                {
                  spaceId,
                  spaceProductId,
                  limit: PAGE_SIZE,
                  cursorPublishedAt: last.publishedAt,
                  cursorId: last.id,
                  ...(feedFilter === "all" && selectedCategoryId
                    ? { categoryId: selectedCategoryId }
                    : {}),
                },
                feedFilter
              );
              if (typeof res === "string") {
                return;
              }
              const appended = [...feedRef.current, ...res.list.map(normalizeFeedOnlyPost)];
              const nextFeed = cacheScope ? trimViewpointFeedStream(appended) : appended;
              const nextHasMore = res.hasMore || nextFeed.length < appended.length;
              setHasMoreOlder(nextHasMore);
              setFeedItems(nextFeed);
              if (cacheScope) {
                saveViewpointFeedCache(
                  cacheScope,
                  pinnedRef.current,
                  nextFeed,
                  nextHasMore,
                  refreshedAtRef.current
                );
              }
            } finally {
              setLoadingMore(false);
            }
          })();
        },
        { root, rootMargin: "120px", threshold: 0 }
      );
      ob.observe(el);
      return () => ob.disconnect();
    }, [
      loading,
      loadingMore,
      hasMoreOlder,
      spaceId,
      spaceProductId,
      selectedCategoryId,
      feedFilter,
      feedItems.length,
      cacheScope,
    ]);

    useEffect(() => {
      const id = window.setInterval(() => {
        void syncNewer();
      }, POLL_MS);
      return () => window.clearInterval(id);
    }, [syncNewer]);

    const patchEngagement = useCallback((id: string, liked: boolean, collected: boolean) => {
      const applyPatch = (r: ViewpointFeedPost): ViewpointFeedPost => {
        if (r.id !== id) return r;
        let likeCount = r.likeCount ?? 0;
        let collectCount = r.collectCount ?? 0;
        const wasLiked = Boolean(r.likedByMe);
        const wasColl = Boolean(r.collectedByMe);
        if (liked !== wasLiked) likeCount += liked ? 1 : -1;
        if (collected !== wasColl) collectCount += collected ? 1 : -1;
        return {
          ...r,
          likedByMe: liked,
          collectedByMe: collected,
          likeCount: Math.max(0, likeCount),
          collectCount: Math.max(0, collectCount),
        };
      };
      const shouldDrop =
        (feedFilter === "liked" && !liked) || (feedFilter === "collected" && !collected);
      setPinnedItems((prevPinned) => {
        const nextPinned = shouldDrop
          ? prevPinned.filter((r) => r.id !== id)
          : prevPinned.map(applyPatch);
        setFeedItems((prevFeed) => {
          const nextFeed = shouldDrop
            ? prevFeed.filter((r) => r.id !== id)
            : prevFeed.map(applyPatch);
          const row = nextPinned.find((r) => r.id === id) ?? nextFeed.find((r) => r.id === id);
          if (cacheScope) {
            if (shouldDrop) {
              saveViewpointFeedCache(
                cacheScope,
                nextPinned,
                nextFeed,
                hasMoreOlderRef.current,
                refreshedAtRef.current
              );
            } else if (row) {
              updateViewpointFeedCacheItem(
                cacheScope,
                id,
                {
                  likedByMe: row.likedByMe,
                  collectedByMe: row.collectedByMe,
                  likeCount: row.likeCount,
                  collectCount: row.collectCount,
                },
                nextPinned,
                nextFeed
              );
            }
          }
          return nextFeed;
        });
        return nextPinned;
      });
    }, [cacheScope, feedFilter]);

    const onToggleLike = useCallback(
      async (e: React.MouseEvent, row: ViewpointFeedPost) => {
        e.stopPropagation();
        const res = await ViewpointFeedApi.toggleLike({
          spaceId,
          spaceProductId,
          viewpointId: row.id,
        });
        if (typeof res === "string") return;
        patchEngagement(row.id, res.liked, res.collected);
      },
      [spaceId, spaceProductId, patchEngagement]
    );

    const onToggleCollect = useCallback(
      async (e: React.MouseEvent, row: ViewpointFeedPost) => {
        e.stopPropagation();
        const res = await ViewpointFeedApi.toggleCollect({
          spaceId,
          spaceProductId,
          viewpointId: row.id,
        });
        if (typeof res === "string") return;
        patchEngagement(row.id, res.liked, res.collected);
      },
      [spaceId, spaceProductId, patchEngagement]
    );

    const onReportClick = useCallback(
      (e: React.MouseEvent, row: ViewpointFeedPost) => {
        e.stopPropagation();
        if (!globalModel.isLoggedIn) {
          const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
          h5NavigateTo(navigate, `/login?redirect=${redirect}`);
          return;
        }
        setReportTarget(row);
        setReportOpen(true);
      },
      [navigate, location.pathname, location.search]
    );

    const closeReportModal = useCallback(() => {
      setReportOpen(false);
      setReportTarget(null);
    }, []);

    const showReportActions = globalModel.financeShareEnabled;

    const onConceptClick = useCallback(
      (concept: ConceptBrief) => {
        const pid = conceptSpaceProductId?.trim();
        const code = concept.code?.trim();
        if (!pid || !code) {
          showConceptLibraryDesktopTipModal();
          return;
        }
        const q = `?concept=${encodeURIComponent(code)}&search=1`;
        h5NavigateTo(navigate, `/space/${spaceId}/${pid}${q}`);
      },
      [conceptSpaceProductId, navigate, spaceId]
    );

    const showEnd = !loading && feedItems.length > 0 && !hasMoreOlder;
    const showFilteredEmpty = !loading && feedItems.length === 0 && pinnedItems.length === 0;

    const onPickFeedCategory = useCallback(
      (categoryId: string) => {
        const id = categoryId.trim();
        if (!id) return;
        const nextId = selectedCategoryId === id ? null : id;
        setSelectedCategoryId(nextId);
        if (nextId !== null) {
          setFeedFilter("all");
        }
      },
      [selectedCategoryId]
    );

    const onPickFeedFilter = useCallback((key: FeedListFilter) => {
      setFeedFilter(key);
      if (key === "all" || key === "liked" || key === "collected") {
        setSelectedCategoryId(null);
      }
    }, []);

    const validFeedCategories = useMemo(
      () =>
        feedCategories.filter((c) => {
          const nm = c.name?.trim();
          const sid = c.id?.trim();
          return Boolean(nm && sid);
        }),
      [feedCategories]
    );

    const tabsScrollFadeRight = useTabsScrollFadeRight(
      tabsScrollRef,
      validFeedCategories.length
    );

    const ptrVisible = pullDistance > 0 || pullPhase === "refreshing";

    return (
      <div
        className="lf-vp-feed lf-vp-feed--h5 lf-vp-feed--moments"
        style={viewpointFeedLayoutCssVars()}
      >
        <div className="lf-vp-feed__inner">
          <div className="lf-vp-feed__toolbar" aria-label="列表筛选与刷新">
            <div
              ref={tabsRowRef}
              className="lf-vp-feed__tabs-row"
              role="tablist"
              aria-label="观点分类"
            >
              <button
                type="button"
                role="tab"
                aria-selected={feedFilter === "all" && !selectedCategoryId}
                className={
                  feedFilter === "all" && !selectedCategoryId
                    ? "lf-vp-feed__filter lf-vp-feed__filter--on lf-vp-feed__filter--pinned"
                    : "lf-vp-feed__filter lf-vp-feed__filter--pinned"
                }
                onClick={() => onPickFeedFilter("all")}
              >
                全部
              </button>
              <div
                className={
                  tabsScrollFadeRight
                    ? "lf-vp-feed__tabs-scroll-wrap lf-vp-feed__tabs-scroll-wrap--fade-right"
                    : "lf-vp-feed__tabs-scroll-wrap"
                }
              >
                <div ref={tabsScrollRef} className="lf-vp-feed__tabs-scroll">
                <div className="lf-vp-feed__tabs-scroll-inner">
                  {(
                    [
                      ["liked", "我点赞的"],
                      ["collected", "我收藏的"],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={feedFilter === key}
                      className={
                        feedFilter === key
                          ? "lf-vp-feed__filter lf-vp-feed__filter--on"
                          : "lf-vp-feed__filter"
                      }
                      onClick={() => onPickFeedFilter(key)}
                    >
                      {label}
                    </button>
                  ))}
                  {validFeedCategories.map((c) => {
                    const nm = c.name!.trim();
                    const on = selectedCategoryId === c.id.trim();
                    return (
                      <button
                        key={c.id}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        className={
                          on ? "lf-vp-feed__filter lf-vp-feed__filter--on" : "lf-vp-feed__filter"
                        }
                        onClick={() => onPickFeedCategory(c.id)}
                      >
                        {nm}
                      </button>
                    );
                  })}
                </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="lf-vp-feed__refresh"
              onClick={() => void syncNewer()}
              disabled={loading || syncing}
              aria-label="立即刷新列表"
            >
              更新于 {refreshedAtLabel(refreshedAt)}
              {syncing ? " · 同步中" : ""}
            </button>
          </div>

          <div ref={bodyScrollRef} className="lf-vp-feed__body">
            <div
              className={
                ptrVisible
                  ? "lf-vp-feed__ptr lf-vp-feed__ptr--visible"
                  : "lf-vp-feed__ptr"
              }
              style={{ height: ptrVisible ? `${pullDistance}px` : 0 }}
              aria-hidden={!ptrVisible}
            >
              <span className="lf-vp-feed__ptr-text">
                {pullPhase === "refreshing" ? (
                  <>
                    <Spin size="small" /> {pullRefreshHint(pullPhase)}
                  </>
                ) : (
                  pullRefreshHint(pullPhase)
                )}
              </span>
            </div>

            <Spin spinning={loading && pullPhase !== "refreshing"}>
              <div className="lf-vp-feed__list">
              {showFilteredEmpty ? (
                <div className="lf-vp-feed__empty-filter">当前筛选下暂无内容</div>
              ) : null}
              {displayItems.map((row) => {
                const nick = nicknameOf(row);
                const titleText =
                  row.titleMode && row.title?.trim() ? row.title.trim() : null;
                const categoryName = row.categoryName?.trim() || "";
                const categoryId = row.categoryId?.trim() || "";
                const showImportance =
                  row.importance === "important" || row.importance === "urgent";
                const imageUrls = row.imageUrls?.slice(0, 9) ?? [];
                const imageGridClass =
                  imageUrls.length === 1
                    ? "lf-vp-moment-images lf-vp-moment-images--single"
                    : "lf-vp-moment-images";

                return (
                  <article key={row.id} className="lf-vp-moment">
                    <EasyAvatar
                      shape="square"
                      squareRadius={4}
                      size={40}
                      url={resolveAppAvatarUrl(row.publisherAvatarUrl) || undefined}
                      word={avatarDisplayWord(nick, row.publisherLabel)}
                      alt=""
                      className="lf-vp-moment-avatar"
                    />
                    <div className="lf-vp-moment-main">
                      <div className="lf-vp-moment-head">
                        <div className="lf-vp-moment-head-left">
                          <span className="lf-vp-moment-nick">{nick}</span>
                          <span className="lf-vp-moment-time">
                            {displayPublishedCompact(row.publishedAt)}
                          </span>
                          {row.pinned ? (
                            <span className="lf-vp-pinned-badge">置顶</span>
                          ) : null}
                        </div>
                        {categoryName && categoryId ? (
                          <button
                            type="button"
                            className="lf-vp-moment-category"
                            onClick={() => onPickFeedCategory(categoryId)}
                          >
                            {categoryName}
                          </button>
                        ) : null}
                      </div>
                      {titleText ? (
                        <div className="lf-vp-moment-title">{titleText}</div>
                      ) : null}
                      <ViewpointConceptLinkedBody
                        content={row.content}
                        catalog={conceptCatalog}
                        onConceptClick={onConceptClick}
                        importancePrefix={
                          showImportance ? (
                            <span className={importanceClass(row.importance)}>
                              {importanceLabel(row.importance)}
                            </span>
                          ) : null
                        }
                        hotBadge={<ViewpointHotBadge likeCount={row.likeCount ?? 0} />}
                      />
                      {imageUrls.length > 0 ? (
                        <div className={imageGridClass}>
                          <Image.PreviewGroup preview={{ destroyOnClose: true }}>
                            {imageUrls.map((u, idx) => (
                              <div key={`${idx}-${u}`} className="lf-vp-moment-image-cell">
                                <Image src={u} alt="" className="lf-vp-moment-image" />
                              </div>
                            ))}
                          </Image.PreviewGroup>
                        </div>
                      ) : null}
                      <ViewpointConceptTagRow
                        content={row.content}
                        catalog={conceptCatalog}
                        onConceptClick={onConceptClick}
                      />
                      <div className="lf-vp-moment-footer">
                        {showReportActions ? (
                          <ViewpointFeedReportAction row={row} onReport={onReportClick} />
                        ) : null}
                        <ViewpointFeedEngagementActions
                          row={row}
                          onToggleLike={onToggleLike}
                          onToggleCollect={onToggleCollect}
                        />
                      </div>
                    </div>
                  </article>
                );
              })}
              <div ref={sentinelRef} className="lf-vp-feed__sentinel" aria-hidden />
              {loadingMore ? (
                <div className="lf-vp-feed__loading-more">
                  <Spin size="small" /> 加载中…
                </div>
              ) : null}
              {showEnd ? <div className="lf-vp-feed__end">已经到底啦</div> : null}
            </div>
          </Spin>
          </div>
        </div>
        <ViewpointReportModal
          open={reportOpen}
          row={reportTarget}
          spaceId={spaceId}
          spaceProductId={spaceProductId}
          onClose={closeReportModal}
        />
      </div>
    );
  }
);

export default ViewpointFeedPage;
